// DashBoard.tsx
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Text,
  Grid,
  GridItem,
  Heading,
  Stack,
  Flex,
  useColorModeValue,
  VStack,
  useColorMode,
} from "@chakra-ui/react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebase";
import { fetchCheckInRecords } from "../../utils/checkInOutUtils";
import {
  eachDayOfInterval,
  endOfMonth,
  getWeek,
  getWeeksInMonth,
  isWeekend,
  startOfMonth,
} from "date-fns";
import BurnoutTrend from "./BurnoutTrend";
import TaskView from "./TaskView";
import { Bar, Pie, Line } from "react-chartjs-2";
import CustomDropdown from "../../components/CustomDropdown";

type CheckinRecord = {
  checkInTime: Date;
  checkOutTime?: Date;
  isCheckedOut: boolean;
};
type TaskRecord = { createdAt: Date };
// after
export type TaskStatus = "Not-started" | "In-progress" | "completed" | "delay";

interface WeekMetrics {
  week: string;
  workingHours: number;
  tasksAssigned: number;
  moodIcon: string;
  riskLevel: string;
}
interface DashBoardProps {
  organizationId: string;
  userId: string;
  tasks: TaskRecord[];
  totalTasks: number;
  assignedTasks: {
    id: string;
    name: string;
    status: TaskStatus;
    risk?: "Low" | "Medium" | "High";
    plannedStart: string;
  }[];
}

const DashBoard: React.FC<DashBoardProps> = ({
  organizationId,
  userId,
  tasks,
  assignedTasks,
  totalTasks,
}) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedCCYear, setSelectedCCYear] = useState<number>(
    new Date().getFullYear()
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );
  const [checkInRecords, setCheckInRecords] = useState<CheckinRecord[]>([]);
  const [weeksData, setWeeksData] = useState<WeekMetrics[]>([]);
  const totalWeeks = weeksData.length;
  const underCount = weeksData.filter((w) => w.moodIcon === "Underworking").length;
  const normalCount = weeksData.filter((w) => w.moodIcon === "Normal").length;
  const burnCount = weeksData.filter((w) => w.moodIcon === "Burnout").length;

  // Convert to percentages across ALL weeks (fallback/general view)
  const underPct = totalWeeks ? +((underCount / totalWeeks) * 100).toFixed(1) : 0;
  const normalPct = totalWeeks ? +((normalCount / totalWeeks) * 100).toFixed(1) : 0;
  const burnPct = totalWeeks ? +((burnCount / totalWeeks) * 100).toFixed(1) : 0;
  const [loading, setLoading] = useState(true);

  const [monthlyWorkedHours, setMonthlyWorkedHours] = useState(0);
  const [normalMonthHours, setNormalMonthHours] = useState(0);

  // ── NEW STATE for Top Metrics ───────────────────────
  const [metricTotals, setMetricTotals] = useState<{
    totalHours: number;
    completed: number;
    inProgress: number;
    riskCount: number;
    assigned: number;
  }>({
    totalHours: 0,
    completed: 0,
    inProgress: 0,
    riskCount: 0,
    assigned: 0,
  });

  // ── NEW STATE for Overall Chart View ───────────────────────
  const [chartView, setChartView] = useState<"performance" | "delays">(
    "performance"
  );

  // ── NEW STATE for Task Overview Year Filter ───────────────────────
  const [taskOverviewSelectedYear, setTaskOverviewSelectedYear] = useState<
    "all" | number
  >("all");

  // ── Filtered Tasks for Task Overview ───────────────────────
  const filteredTasksForTaskView = useMemo(() => {
    if (taskOverviewSelectedYear === "all") {
      return assignedTasks;
    }
    return assignedTasks.filter(
      (task) =>
        new Date(task.plannedStart).getFullYear() === taskOverviewSelectedYear
    );
  }, [assignedTasks, taskOverviewSelectedYear]);

  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const cardClass = isDark
    ? "bg-[#1A1C1F] border border-[#2E2F33]"
    : "bg-white border border-gray-200";
  const headerBgClass = isDark ? "bg-[#14161B]" : "bg-gray-50";
  const divideClass = isDark ? "divide-[#2E2F33]" : "divide-gray-200";
  const tbodyBgClass = isDark ? "bg-[#1A1C1F]" : "bg-white";
  // Use same table background as UserTable for cards
  const cardBg = isDark ? "#1A1C1F" : "white";
  const cardBorder = isDark ? "#2E2F33" : "gray.200";

  // Weeks to display in User Level Insights (show only worked weeks for current month)
  const displayedWeeks = useMemo(() => {
    if (!weeksData || weeksData.length === 0) return [] as WeekMetrics[];
    const now = new Date();
    const isCurrentMonth =
      selectedMonth === now.getMonth() && selectedCCYear === now.getFullYear();
    // check-ins are for currentYear; if not current month selected:
    if (!isCurrentMonth) {
      // Past months (or future months) — show all if past, none if future
      if (
        selectedCCYear < now.getFullYear() ||
        (selectedCCYear === now.getFullYear() && selectedMonth < now.getMonth())
      )
        return weeksData;
      return [] as WeekMetrics[]; // future month: nothing yet
    }
    const today = now.getDate();
    const currentWeekIndex = today <= 7 ? 0 : today <= 14 ? 1 : today <= 21 ? 2 : 3;
    return weeksData.slice(0, Math.min(currentWeekIndex + 1, weeksData.length));
  }, [weeksData, selectedMonth, selectedCCYear]);

  // Percentages for VISIBLE weeks (current month: only elapsed weeks; others: whole month)
  const { underPctVisible, normalPctVisible, burnPctVisible } = useMemo(() => {
    const now = new Date();
    const isCurrentMonth =
      selectedMonth === now.getMonth() && selectedCCYear === now.getFullYear();
    const base = isCurrentMonth ? displayedWeeks : weeksData;
    const total = base.length;
    if (!total) {
      return { underPctVisible: 0, normalPctVisible: 0, burnPctVisible: 0 };
    }
    const u = base.filter((w) => w.moodIcon === "Underworking").length;
    const n = base.filter((w) => w.moodIcon === "Normal").length;
    const b = base.filter((w) => w.moodIcon === "Burnout").length;
    return {
      underPctVisible: +((u / total) * 100).toFixed(1),
      normalPctVisible: +((n / total) * 100).toFixed(1),
      burnPctVisible: +((b / total) * 100).toFixed(1),
    };
  }, [displayedWeeks, weeksData, selectedMonth, selectedCCYear]);

  // 1️⃣ fetch
  useEffect(() => {
    setLoading(true);
    fetchCheckInRecords(organizationId, userId, 2)
      .then((records) => {
        const recs = records.map((data: any) => {
          return {
            checkInTime: new Date(data.checkInTime),
            checkOutTime: data.checkOutTime
              ? new Date(data.checkOutTime)
              : undefined,
            isCheckedOut: data.isCheckedOut,
          };
        });
        setCheckInRecords(recs);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [organizationId, userId]);

  // 2️⃣ recompute
  useEffect(() => {
    if (!loading) computeMetrics();
  }, [loading, selectedMonth, selectedCCYear, checkInRecords, tasks]);

  function computeMetrics() {
    const year = selectedCCYear;
    const daysInMonth = new Date(year, selectedMonth + 1, 0).getDate();
    const segments = [
      { week: "Week 1", start: 1, end: 7 },
      { week: "Week 2", start: 8, end: 14 },
      { week: "Week 3", start: 15, end: 21 },
      { week: "Week 4", start: 22, end: daysInMonth },
    ];
    const metrics = segments.map(() => ({ workingHours: 0, tasks: 0 }));
    const daysWithDataPerSegment: Array<Set<number>> = segments.map(() => new Set<number>());

    // hours
    checkInRecords.forEach((rec) => {
      const dt = rec.checkInTime;
      if (dt.getMonth() !== selectedMonth || dt.getFullYear() !== year) return;
      const exit = rec.checkOutTime;
      if (rec.isCheckedOut && exit) {
        const idx = segments.findIndex(
          (s) => dt.getDate() >= s.start && dt.getDate() <= s.end
        );
        if (idx >= 0) {
          metrics[idx].workingHours +=
            (exit.getTime() - dt.getTime()) / (1000 * 3600);
          daysWithDataPerSegment[idx].add(dt.getDate());
        }
      }
    });

    // tasks per week
    tasks.forEach(({ createdAt }) => {
      if (
        createdAt.getMonth() !== selectedMonth ||
        createdAt.getFullYear() !== year
      )
        return;
      const idx = segments.findIndex(
        (s) => createdAt.getDate() >= s.start && createdAt.getDate() <= s.end
      );
      if (idx >= 0) metrics[idx].tasks++;
    });

    // build WeekMetrics
    const result = segments.map((s, i) => {
      const wh = metrics[i].workingHours;
      const daysWithData = daysWithDataPerSegment[i].size;
      const avgDaily = daysWithData > 0 ? wh / daysWithData : 0;
      let icon = "Underworking" as WeekMetrics["moodIcon"];
      let risk = "Low" as WeekMetrics["riskLevel"];
      if (avgDaily > 9) {
        icon = "Burnout";
        risk = "High";
      } else if (avgDaily >= 8 && avgDaily <= 9) {
        icon = "Normal";
        risk = "Medium";
      }

      return {
        week: s.week,
        workingHours: +wh.toFixed(1),
        tasksAssigned: metrics[i].tasks,
        moodIcon: icon,
        riskLevel: risk,
      };
    });

    setWeeksData(result);
    // Still inside computeMetrics(), after building `result`:

    // ── NEW: Compute monthlyWorkedHours and normalMonthHours ───
    // 1) Sum up actual hours:
    const totalWorked = +result
      .reduce((sum, w) => sum + w.workingHours, 0)
      .toFixed(1);
    setMonthlyWorkedHours(totalWorked);

    // 2) Count weekdays in the selected month:
    const firstDay = startOfMonth(new Date(year, selectedMonth));
    const lastDay = endOfMonth(new Date(year, selectedMonth));
    const allDays = eachDayOfInterval({ start: firstDay, end: lastDay });
    const weekdays = allDays.filter((day) => !isWeekend(day));
    const normalHours = weekdays.length * 8; // 8 hours per weekday
    setNormalMonthHours(normalHours);
  }

  // ─── Compute Top Metrics for the current calendar month ────────────
  useEffect(() => {
    if (loading) return;

    const currMonth = new Date().getMonth();

    // 1) Sum all worked hours in current month:
    const hrs = checkInRecords.reduce((sum, rec) => {
      if (rec.checkInTime.getMonth() !== currMonth) return sum;
      if (rec.isCheckedOut && rec.checkOutTime) {
        return (
          sum +
          (rec.checkOutTime.getTime() - rec.checkInTime.getTime()) /
            (1000 * 3600)
        );
      }
      return sum;
    }, 0);

    // 2) Count statuses across all assignedTasks (no month filter):
    const completed = assignedTasks.filter(
      (t) => t.status === "completed"
    ).length;
    const inProgress = assignedTasks.filter(
      (t) => t.status === "In-progress"
    ).length;
    const riskCount = assignedTasks.filter((t) => t.risk === "High").length;

    setMetricTotals({
      totalHours: +hrs.toFixed(1),
      completed,
      inProgress,
      riskCount,
      assigned: assignedTasks.length, // or just use totalTasks
    });
  }, [loading, checkInRecords, assignedTasks]);

  // ─── New: monthly breakdown for Assigned vs Completed ─────────────
  const monthLabels = Array.from({ length: 12 }, (_, i) =>
    new Date(0, i).toLocaleString("default", { month: "short" })
  );

  // ─── "Based on Tasks" chart data ────────────────────────────────
  const assignedByMonth = monthLabels.map(
    (_, m) =>
      // count every task whose plannedStart fell in month m of selectedYear
      assignedTasks.filter((t) => {
        const d = new Date(t.plannedStart);
        return d.getFullYear() === selectedYear && d.getMonth() === m;
      }).length
  );

  const completedByMonth = monthLabels.map(
    (_, m) =>
      // same, but only status==="completed"
      assignedTasks.filter((t) => {
        if (t.status !== "completed") return false;
        const d = new Date(t.plannedStart);
        return d.getFullYear() === selectedYear && d.getMonth() === m;
      }).length
  );

  // ─── NEW: Delay Trends data (updated to show Delayed vs Completed) ────────────────────────────────
  const delayedTasksByMonth = monthLabels.map(
    (_, m) =>
      assignedTasks.filter((t) => {
        const d = new Date(t.plannedStart);
        return (
          d.getFullYear() === selectedYear &&
          d.getMonth() === m &&
          t.status === "delay"
        );
      }).length
  );



  return (
    // ─── REPLACE `<Grid …>` with this outer container ────────────
    <div className="container mx-auto px-4 py-1 space-y-6">
      {/** ─── NEW "Top Metrics" SECTION goes here ─────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-3">
        {/* 1️⃣ Total Hours Card */}
        <div
          className="relative p-5 rounded-xl shadow-lg overflow-hidden"
          style={{
            background: "linear-gradient(to top right, #ff7e5f, #feb47b)",
          }}
        >
          <div className="flex justify-between items-start">
            <span className={`text-md font-semibold ${isDark ? 'text-black' : 'text-gray-800'}`}>
              Total Hours Worked
            </span>
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
          </div>
          <div className="mt-1">
            <span className="text-4xl font-bold text-gray-900">
              {metricTotals.totalHours}
              <span className="text-xl font-normal ml-1">Hrs</span>
            </span>
            <span className="block text-sm text-gray-600 mt-1">This Month</span>
          </div>
        </div>

        {/* 2️⃣ Tasks Status Card */}
        <div
          className="relative p-5 rounded-xl shadow-lg overflow-hidden"
          style={{
            background: "linear-gradient(to top right, #a1c4fd, #c2e9fb)",
          }}
        >
          <div className="flex justify-between items-start">
            <span className={`text-md font-semibold ${isDark ? 'text-black' : 'text-gray-800'}`}>
              Tasks: Completed / In Progress
            </span>
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                ></path>
              </svg>
            </div>
          </div>
          <div className="mt-1 grid grid-cols-2 gap-4">
            <div className="flex flex-col">
              <span className="text-3xl font-bold text-gray-900">
                {metricTotals.completed}
              </span>
              <span className="text-sm text-gray-600 mt-1">Completed</span>
            </div>
            <div className="flex flex-col">
              <span className="text-3xl font-bold text-gray-900">
                {metricTotals.inProgress}
              </span>
              <span className="text-sm text-gray-600 mt-1">In Progress</span>
            </div>
          </div>
        </div>

        {/* 3️⃣ Risk Assessment Card */}
        <div
          className="relative p-5 rounded-xl shadow-lg overflow-hidden"
          style={{
            background: "linear-gradient(to top right, #fbc2eb, #a6c1ee)",
          }}
        >
          <div className="flex justify-between items-start">
            <span className={`text-md font-semibold ${isDark ? 'text-black' : 'text-gray-800'}`}>
              Tasks: Assigned / Risk
            </span>
            <div className="w-8 h-8 rounded-full bg-white flex items-center justify-center shadow-sm">
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                ></path>
              </svg>
            </div>
          </div>
          <div className="mt-1 grid grid-cols-2 gap-4">
            <div className="flex flex-col">
              <span className="text-3xl font-bold text-gray-900">
                {metricTotals.assigned}
              </span>
              <span className="text-sm text-gray-600 mt-1">Assigned</span>
            </div>
            <div className="flex flex-col">
              <span className="text-3xl font-bold text-gray-900">
                {metricTotals.riskCount}
              </span>
              <span className="text-sm text-gray-600 mt-1">High Risk</span>
            </div>
          </div>
        </div>
      </div>
      {/** ─── END of "Top Metrics" SECTION ───────────────────────── */}
      {/* ─────────────────────────────────────────────────────────────────────── */}
      {/* ─── Section Title & Month Selector (Check-In/Out) ───────────────────────────────────────── */}
      <Flex
        justify="space-between"
        align="center"
        mb={6}
        pb={2}
        borderBottom="1px"
        borderColor="gray.200"
      >
        <Text fontSize="xl" fontWeight="bold" color={isDark ? 'gray.200' : 'gray.800'}>
          Based on Check-In and Check-Out
        </Text>
        <div className="flex gap-2">
          <CustomDropdown
            value={selectedCCYear.toString()}
            onChange={(value) => setSelectedCCYear(+value)}
            isDark={isDark}
            className="w-24 h-8 text-sm"
            options={Array.from({ length: 7 }, (_, i) => {
              const yr = new Date().getFullYear() - i;
              return { value: yr.toString(), label: yr.toString() };
            })}
          />
          <CustomDropdown
            value={selectedMonth.toString()}
            onChange={(value) => setSelectedMonth(+value)}
            isDark={isDark}
            className="w-24 h-8 text-sm"
            options={Array.from({ length: 12 }, (_, i) => ({
              value: i.toString(),
              label: new Date(0, i).toLocaleString("default", { month: "short" })
            }))}
          />
        </div>
      </Flex>
      {/* Full-width Burnout Trend graph */}
      <div className={`rounded-xl p-6 mb-6 ${cardClass}`} style={{ backgroundColor: cardBg, borderColor: "gray.200" }}>
        <h2 className={`text-md font-bold ${isDark ? "text-white" : "text-gray-500"} mb-1`}>Burnout Trend</h2>
        <div className="w-full h-64">
          <BurnoutTrend
            checkInRecords={checkInRecords}
            month={selectedMonth}
            year={selectedCCYear}
          />
        </div>
      </div>
      {/* Two‐column row: Burnout Trend (left) & Pie chart (right) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className={`rounded-lg p-6 ${cardClass}`} style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
          <div className="flex justify-between items-center mb-4">
            <h2 className={`text-md font-semibold ${isDark ? "text-gray-300" : "text-gray-500"}`}>
              User Level Insights
            </h2>
            <span className={`text-xs font-medium ${isDark ? 'text-[#FBD282]' : 'text-[#B59143]'} ${isDark ? 'bg-black border border-[#B59143]' : 'bg-[#FFF7E6] border border-[#E8C16C]'} rounded-full px-2 py-1`}>
              Tasks Assigned: {totalTasks}
            </span>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <svg
                className="animate-spin h-8 w-8 text-gray-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-64">
              <table className={`min-w-full divide-y ${divideClass}`}>
                <thead className={`${headerBgClass}`}>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Week
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      Hrs
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      Risk
                    </th>
                  </tr>
                </thead>
                <tbody className={`${tbodyBgClass} divide-y ${divideClass}`}>
                  {displayedWeeks.map(
                    ({ week, workingHours, moodIcon, riskLevel }) => (
                      <tr key={week}>
                        <td className={`px-4 py-2 whitespace-nowrap text-xs font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {week}
                        </td>
                        <td className={`px-4 py-2 whitespace-nowrap text-xs text-right ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {workingHours}
                        </td>
                        <td className={`px-4 py-2 whitespace-nowrap text-center text-xs ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {moodIcon}
                        </td>
                        <td className={`px-4 py-2 whitespace-nowrap text-center text-xs ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                          {riskLevel}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right: Pie chart (Burnout vs Normal) */}
        <div className={`rounded-lg p-4 ${cardClass}`} style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
          <h2 className={`text-md font-semibold mb-2 ${isDark ? "text-white" : "text-gray-500"}`}>
            Burnout Level
          </h2>
          <div className="h-48">
            <Pie
              data={
                monthlyWorkedHours > 0
                  ? {
                      labels: ["Underworking", "Normal", "Burnout"],
                      datasets: [
                        {
                          data: [underPctVisible, normalPctVisible, burnPctVisible],
                          backgroundColor: [
                            "#FDE68A",
                            "#93C5FD",
                            "#FCA5A5",
                          ],
                          borderColor: "#ffffff",
                          borderWidth: 1,
                        },
                      ],
                    }
                  : {
                      labels: ["No data"],
                      datasets: [
                        {
                          data: [100],
                          backgroundColor: ["#D1D5DB"],
                          borderColor: "#ffffff",
                          borderWidth: 1,
                        },
                      ],
                    }
              }
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "bottom",
                    labels: {
                      boxWidth: 12,
                      boxHeight: 12,
                      padding: 30,
                      color: isDark ? "#FFFFFF" : "#2D3748",
                    },
                  },
                  tooltip: {
                    callbacks: {
                      label: function (context) {
                        if (monthlyWorkedHours <= 0) return "No data";
                        const label = context.label;
                        const pct = context.dataset.data![
                          context.dataIndex
                        ] as number;
                        return `${label}: ${pct}%`;
                      },
                    },
                  },
                },
                layout: {
                  padding: { bottom: 20 },
                },
              }}
            />
          </div>
        </div>
      </div>
      {/* ─────────────────────────────────────────────────────────────────────── */}
      {/* ─── Section Title & Year Selector ───────────────────────────────────────── */}
      <Flex
        justify="space-between"
        align="center"
        mb={4}
        pb={2}
        borderBottom="1px"
        borderColor="gray.200"
      >
        <Text fontSize="xl" fontWeight="bold" color={isDark ? 'gray.200' : 'gray.800'}>
          Based on Tasks
        </Text>
        <CustomDropdown
          value={selectedYear.toString()}
          onChange={(value) => setSelectedYear(+value)}
          isDark={isDark}
          className="w-24 h-8 text-sm"
          options={Array.from({ length: 5 }, (_, i) => {
            const yr = new Date().getFullYear() - i;
            return { value: yr.toString(), label: yr.toString() };
          })}
        />
      </Flex>
      {/* ─── Task Overview and Overall Chart Section ───────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row gap-6 h-full">
        {/* Task Overview - Left (35%) */}
        <div className="w-full md:w-[35%] flex flex-col">
          <div className={`rounded-lg p-4 flex flex-col flex-grow ${cardClass}`} style={{ backgroundColor: cardBg, borderColor: cardBorder }}>
            <div className="flex justify-between items-center mb-2">
              <h2 className={`text-md font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-500"}`}>
                Task Overview
              </h2>
              <CustomDropdown
                value={taskOverviewSelectedYear.toString()}
                onChange={(value) =>
                  setTaskOverviewSelectedYear(
                    value === "all" ? "all" : +value
                  )
                }
                isDark={isDark}
                className="w-24 h-8 text-sm"
                options={[
                  { value: "all", label: "Overall" },
                  ...Array.from({ length: 5 }, (_, i) => {
                    const yr = new Date().getFullYear() - i;
                    return { value: yr.toString(), label: yr.toString() };
                  })
                ]}
              />
            </div>
            <div className="flex-grow h-full ">
              <TaskView
                tasks={filteredTasksForTaskView}
                length={filteredTasksForTaskView.length}
              />
            </div>
            {/* New: Task Status Summary */}
            <div className={`mt-1 p-4 ${isDark ? 'bg-[#2A3441]' : 'bg-gray-50'} rounded-lg flex flex-wrap justify-around gap-4`}>
              <div className="flex items-center space-x-1">
                <div
                  className="w-3 h-3 square-full"
                  style={{ backgroundColor: "#F87171" }}
                ></div>
                <span
                  className="text-sm font-bold"
                  style={{ color: "#F87171" }}
                >
                  {assignedTasks.filter((t) => t.status === "delay").length}
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <div
                  className="w-3 h-3 square-full"
                  style={{ backgroundColor: "#FFE066" }}
                ></div>
                <span
                  className="text-sm font-bold"
                  style={{ color: "#FFE066" }}
                >
                  {
                    assignedTasks.filter((t) => t.status === "Not-started")
                      .length
                  }
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <div
                  className="w-3 h-3 square-full"
                  style={{ backgroundColor: "#A0C4FF" }}
                ></div>
                <span
                  className="text-sm font-bold"
                  style={{ color: "#A0C4FF" }}
                >
                  {
                    assignedTasks.filter((t) => t.status === "In-progress")
                      .length
                  }
                </span>
              </div>
              <div className="flex items-center space-x-1">
                <div
                  className="w-3 h-3 square-full"
                  style={{ backgroundColor: "#B2F2BB" }}
                ></div>
                <span
                  className="text-sm font-bold"
                  style={{ color: "#B2F2BB" }}
                >
                  {assignedTasks.filter((t) => t.status === "completed").length}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Overall Chart - Right (65%) */}
        <div className="w-full md:w-[65%] flex flex-col">
          <Box
            bg={cardBg}
            borderWidth="1px"
            borderColor={cardBorder}
            borderRadius="lg"
            p={4}
            flexGrow={1}
            display="flex"
            flexDirection="column"
          >
            <div className="flex justify-between items-center mb-4">
              <Heading size="md" color={isDark ? "gray.300" : "gray.500"}>
                Overall
              </Heading>
              <div className="flex space-x-2">
                <button
                  onClick={() => setChartView("performance")}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    chartView === "performance"
                      ? isDark 
                        ? "bg-black text-[#FBD282] border border-[#B59143]"
                        : "bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C]"
                      : isDark
                      ? "bg-[#2A3441] text-gray-300 border border-[#2E2F33]"
                      : "bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  Performance
                </button>
                <button
                  onClick={() => setChartView("delays")}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${
                    chartView === "delays"
                      ? isDark 
                        ? "bg-black text-[#FBD282] border border-[#B59143]"
                        : "bg-[#FFF7E6] text-[#B59143] border border-[#E8C16C]"
                      : isDark
                      ? "bg-[#2A3441] text-gray-300 border border-[#2E2F33]"
                      : "bg-gray-200 text-gray-700 border border-gray-300"
                  }`}
                >
                  Delays
                </button>
              </div>
            </div>

            <Box flexGrow={1}>
              {chartView === "performance" ? (
                <Bar
                  data={{
                    labels: monthLabels,
                    datasets: [
                      {
                        label: "Assigned",
                        data: assignedByMonth,
                        backgroundColor: "#3B82F6",
                      },
                      {
                        label: "Completed",
                        data: completedByMonth,
                        backgroundColor: "#10B981",
                      },
                    ],
                  }}
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    scales: {
                      x: { 
                        grid: { display: false },
                        ticks: { color: isDark ? "#E2E8F0" : "#2D3748" },
                      },
                      y: {
                        beginAtZero: true,
                        min: 0,
                        max: 12,
                        ticks: { 
                          stepSize: 2,
                          color: isDark ? "#E2E8F0" : "#2D3748",
                        },
                        grid: {
                          color: isDark ? "#4A5568" : "#E2E8F0",
                        },
                        border: {
                          color: isDark ? "#4A5568" : "#E2E8F0",
                        },
                      },
                    },
                    plugins: { legend: { display: false } },
                  }}
                />
              ) : (
                <Line
                  data={{
                    labels: monthLabels,
                    datasets: [
                      {
                        label: "Delayed",
                        data: delayedTasksByMonth,
                        borderColor: "#F87171", // Red for Delayed
                        backgroundColor: "#F87171",
                        fill: false,
                        tension: 0.4,
                      },
                      {
                        label: "Completed",
                        data: completedByMonth,
                        borderColor: "#A0C4FF", // Blue for Completed
                        backgroundColor: "#A0C4FF",
                        fill: false,
                        tension: 0.4,
                      },
                    ],
                  }}
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    scales: {
                      x: { 
                        grid: { display: false },
                        ticks: { color: isDark ? "#E2E8F0" : "#2D3748" },
                      },
                      y: {
                        beginAtZero: true,
                        min: 0,
                        max: Math.max(
                          ...delayedTasksByMonth,
                          ...completedByMonth,
                          4
                        ), // Adjust max based on new data
                        ticks: { 
                          stepSize: 1,
                          color: isDark ? "#E2E8F0" : "#2D3748",
                        },
                        grid: {
                          color: isDark ? "#4A5568" : "#E2E8F0",
                        },
                        border: {
                          color: isDark ? "#4A5568" : "#E2E8F0",
                        },
                      },
                    },
                    plugins: {
                      legend: {
                        position: "bottom",
                        labels: { 
                          boxWidth: 12, 
                          boxHeight: 12, 
                          padding: 20,
                          color: isDark ? "#FFFFFF" : "#2D3748",
                        },
                      },
                    },
                  }}
                />
              )}
            </Box>
            <div className="flex justify-end mt-4 justify-center">
              {chartView === "performance" && (
                <div className="flex space-x-6">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 square-full bg-blue-600"></div>
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Assigned</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 square-full bg-green-500"></div>
                    <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Completed</span>
                  </div>
                </div>
              )}
              {/* No legend needed for delays as it's handled by chart plugins */}
            </div>
          </Box>
        </div>
      </div>
    </div>
  );
};

export default DashBoard;
